import React, { PureComponent, Fragment, createRef } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCVCElement,
  PostalCodeElement,
} from 'react-stripe-elements';
import moment from 'moment-timezone';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import { DatePicker, DropdownContainer, CardSelect } from "../index";
import { Button } from '../../components';
import {
  InvalidCross,
  getCardBrandIcon,
  axiosInstance,
} from '../../utils';

import {
  OptionsSubHeading,
  RecipientList,
  RecipientInputContainer,
  RecipientCurrency,
  RecipientLabel,
  RecipientInput,
  RecipientInputMasked,
  CardInputContainer,
  CardInputRow,
  ClientInfoHeading,
  ClientInfoList,
  ClientInfoNote,
  InfoInputErrorTooltip,
  InfoInputContainer,
  StripeInput,
  CardIcon,
  InfoInput,
  GuestLoginContainer,
  GuestLoginHeading,
  GuestLoginButtonContainer,
  PaymentProcessError,
  baseInputStyle,
} from '../PaymentForm/styles';

const userTimeZone = new Date().getTimezoneOffset();

class PaymentWizardGift extends PureComponent {
  static propTypes = {
    setFormikInstance: PropTypes.func,
    price: PropTypes.number,
    setPriceAmount: PropTypes.func,
    setUpFieldInstances: PropTypes.func,
    handleFormSubmit: PropTypes.func,
    handleLoginButtonClick: PropTypes.func,
    handleCardElementsInput: PropTypes.func,
    baseInitialValues: PropTypes.instanceOf(Object),
    baseScheme: PropTypes.instanceOf(Object),
    cardErrors: PropTypes.instanceOf(Object),
    userData: PropTypes.instanceOf(Object),
    status: PropTypes.string,
    brand: PropTypes.string,
    payingWith: PropTypes.instanceOf(Object),
    error: PropTypes.string,
  };

  state = {
    isOpen: false,
    selectedDate: moment().format('YYYY-MM-DD'),
  };

  form = createRef();

  cardNumber = createRef();

  cardExpiry = createRef();

  cardCvc = createRef();

  postalCode = createRef();

  componentDidMount() {
    const { selectedDate } = this.state;
    const { setFormikInstance, setUpFieldInstances } = this.props;

    setFormikInstance(this.form.current);
    setUpFieldInstances(this.cardNumber.current, this.cardExpiry.current, this.cardCvc.current, this.postalCode.current);

    this.form.current.setFieldValue('deliveryDate', selectedDate);
  }

  componentDidUpdate(prevProps) {
    const { userData: oldData, payingWith: oldPaymentMethod } = prevProps;
    const { userData: newData, payingWith: newPaymentMethod, setUpFieldInstances } = this.props;
    const { selectedDate } = this.state;

    if (JSON.stringify(oldData) !== JSON.stringify(newData) || JSON.stringify(oldPaymentMethod)!== JSON.stringify(newPaymentMethod)) {
      setUpFieldInstances(this.cardNumber.current, this.cardExpiry.current, this.cardCvc.current, this.postalCode.current);
      this.form.current.setFieldValue('deliveryDate', selectedDate);
    }
  }

  debounced = (setPriceAmount, value) => debounce(() => setPriceAmount(value), 300)();

  handleDropdown = () => {
    this.setState(prevState => ({
      isOpen: !prevState.isOpen,
    }))
  };

  closeDropdown = () => {
    this.setState({
      isOpen: false,
    })
  };

  handleDateSelection = date => {
    this.setState({
      selectedDate: moment(date).subtract(userTimeZone,'minute'),
    });
    this.form.current.setFieldValue('deliveryDate', moment(date).subtract(userTimeZone,'minute'));
    this.closeDropdown();
  };

  onMessageClick = () => {
    const { state: { values }, initialValues, setFieldValue } = this.form.current;

    if (values.recipientMessage === initialValues.recipientMessage) {
      setFieldValue('recipientMessage', '');
    }
  };

  onEmailBlur = async (evt, formikHandleBlurFunc) => {
    formikHandleBlurFunc(evt);

    if (!this.form.current.state.errors.emailAddress) {
      try {
        const { target: { value } } = evt;
        await axiosInstance.post('users/validate_email', { email: value });
      } catch (error) {
        this.form.current.setFieldError('emailAddress', error)
      }
    }
  };

  onPriceAmountChange = (evt, formikHandleChangeFunc) => {
    const { setPriceAmount } = this.props;
    const { target: { value } } = evt;
    this.debounced(setPriceAmount, value);

    formikHandleChangeFunc(evt);
  };

  onFormSubmit = values => {
    const { selectedDate } = this.state;
    const { handleFormSubmit, price } = this.props;
    this.form.current.setFieldValue('deliveryDate', moment(selectedDate).format('YYYY-MM-DD'));
    handleFormSubmit({ ...values, price }, 'gift');
  };

  render() {
    const { handleDropdown, closeDropdown, handleDateSelection, onFormSubmit, onPriceAmountChange, onMessageClick, onEmailBlur } = this;
    const { isOpen, selectedDate } = this.state;
    const { cardErrors, brand, handleLoginButtonClick, userData, status, handleCardElementsInput, baseScheme, baseInitialValues, payingWith, error } = this.props;
    const { cardNumber, cardExpiry, cardCvc, postalCode } = cardErrors;

    const WizardScheme = Yup.object().shape({
      ...baseScheme,
      price: Yup.number().min(20, 'Minimum gift card amount is $20'),
      recipientName: Yup.string().required('Friend\'s name is required'),
      recipientEmail: Yup.string().email('Invalid email').required('Email is required'),
      recipientMessage: Yup.string(),
    });

    const CardWizardScheme = Yup.object().shape({
      price: Yup.number().min(20, 'Minimum gift card amount is $20'),
      recipientName: Yup.string().required('Friend\'s name is required'),
      recipientEmail: Yup.string().email('Invalid email').required('Email is required'),
      recipientMessage: Yup.string(),
    });

    const initialValues = {
      ...baseInitialValues,
      price: 20,
      deliveryDate: '',
      recipientName: '',
      recipientEmail: '',
      recipientMessage: 'Enjoy the gift of Dayaxe from me!',
    };

    return (
      <article>
        <GuestLoginContainer>
          <GuestLoginHeading>Confirm and pay</GuestLoginHeading>
          {!userData && (
            <GuestLoginButtonContainer>
              <Button htmlType="button" theme="secondary" onClick={() => handleLoginButtonClick('login')}>Login</Button>
            </GuestLoginButtonContainer>
          )}
          {status === 'hasAccount' && <PaymentProcessError>Looks like you already have an account. Please, log in</PaymentProcessError>}
          {status === 'failure' && <PaymentProcessError>{error || <Fragment>There is a problem with your purchase. Our engineers have been notified. Please try again later or contact our customer service for more details.</Fragment>}</PaymentProcessError>}
        </GuestLoginContainer>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={payingWith && (payingWith.value === 'newCard' || payingWith.value === 'paypal') ? WizardScheme : CardWizardScheme}
          onSubmit={onFormSubmit}
          ref={this.form}
        >
          {({ errors, touched, handleBlur, handleChange, values }) => (
            <Form>
              <RecipientList>
                <li>
                  <div>
                    <RecipientInputContainer as="button" type="button" onClick={handleDropdown}>
                      <RecipientLabel>Delivery date</RecipientLabel>
                      <RecipientInput as="span">{moment(selectedDate).format('MMM DD, YYYY')}</RecipientInput>
                    </RecipientInputContainer>
                    {isOpen && (
                      <DropdownContainer name="calendar" onClickOutside={closeDropdown} handleCloseButtonClick={closeDropdown}>
                        <DatePicker handleDayClick={handleDateSelection} />
                      </DropdownContainer>
                    )}
                  </div>
                  <RecipientInputContainer htmlFor="price">
                    <RecipientLabel>Gift Amount</RecipientLabel>
                    <RecipientCurrency>$</RecipientCurrency>
                    <RecipientInputMasked
                      style={{ marginLeft: 10 }}
                      mask={[/[1-9]/, /\d/, /\d/]}
                      guide={false}
                      min="20"
                      type="number"
                      name="price"
                      id="price"
                      value={values.price}
                      onChange={evt => onPriceAmountChange(evt, handleChange)}
                      onBlur={handleBlur}
                      placeholder="50"
                    />
                    {errors.price && touched.price && (
                      <InfoInputErrorTooltip
                        content={errors.price}
                        placement="left"
                        fadeDuration={100}
                        padding={20}
                        radius={6}
                        background="#ffffff"
                        color="#5a5a5a"
                        border="#ebebeb"
                      >
                        <InvalidCross color="#ff4e72" />
                      </InfoInputErrorTooltip>
                    )}
                  </RecipientInputContainer>
                </li>
                <li>
                  <RecipientInputContainer htmlFor="recipientName">
                    <RecipientLabel>Friend&apos;s name</RecipientLabel>
                    <RecipientInput
                      type="text"
                      name="recipientName"
                      id="recipientName"
                      value={values.recipientName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter friend's name"
                    />
                    {errors.recipientName && touched.recipientName && (
                      <InfoInputErrorTooltip
                        content={errors.recipientName}
                        placement="left"
                        fadeDuration={100}
                        padding={20}
                        radius={6}
                        background="#ffffff"
                        color="#5a5a5a"
                        border="#ebebeb"
                      >
                        <InvalidCross color="#ff4e72" />
                      </InfoInputErrorTooltip>
                    )}
                  </RecipientInputContainer>
                </li>
                <li>
                  <RecipientInputContainer htmlFor="recipientEmail">
                    <RecipientLabel>Friend&apos;s email</RecipientLabel>
                    <RecipientInput
                      type="text"
                      name="recipientEmail"
                      id="recipientEmail"
                      value={values.recipientEmail}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter friend's email"
                    />
                    {errors.recipientEmail && touched.recipientEmail && (
                      <InfoInputErrorTooltip
                        content={errors.recipientEmail}
                        placement="left"
                        fadeDuration={100}
                        padding={20}
                        radius={6}
                        background="#ffffff"
                        color="#5a5a5a"
                        border="#ebebeb"
                      >
                        <InvalidCross color="#ff4e72" />
                      </InfoInputErrorTooltip>
                    )}
                  </RecipientInputContainer>
                </li>
                <li onClick={onMessageClick}>
                  <RecipientInputContainer htmlFor="recipientMessage">
                    <RecipientLabel>Message</RecipientLabel>
                    <RecipientInput
                      type="text"
                      name="recipientMessage"
                      id="recipientMessage"
                      value={values.recipientMessage}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter message to friend"
                    />
                    {errors.recipientMessage && touched.recipientMessage && (
                      <InfoInputErrorTooltip
                        content={errors.recipientMessage}
                        placement="left"
                        fadeDuration={100}
                        padding={20}
                        radius={6}
                        background="#ffffff"
                        color="#5a5a5a"
                        border="#ebebeb"
                      >
                        <InvalidCross color="#ff4e72" />
                      </InfoInputErrorTooltip>
                    )}
                  </RecipientInputContainer>
                </li>
              </RecipientList>
              <OptionsSubHeading>Pay with:</OptionsSubHeading>
              <CardSelect noCredits />
              {payingWith && (payingWith.value === 'newCard' || payingWith.value === 'paypal') ? (
                <Fragment>
                  <ClientInfoList>
                    <li>
                      <ClientInfoHeading htmlFor="firstName">First name</ClientInfoHeading>
                      <InfoInputContainer hasError={errors.firstName && touched.firstName}>
                        <InfoInput
                          mask={false}
                          value={values.firstName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="firstName"
                          id="firstName"
                          placeholder="Enter first name"
                          type="text"
                        />
                        {errors.firstName && touched.firstName && (
                          <InfoInputErrorTooltip
                            content={errors.firstName}
                            placement="left"
                            fadeDuration={100}
                            padding={20}
                            radius={6}
                            background="#ffffff"
                            color="#5a5a5a"
                            border="#ebebeb"
                          >
                            <InvalidCross color="#ff4e72" />
                          </InfoInputErrorTooltip>
                        )}
                      </InfoInputContainer>
                    </li>
                    <li>
                      <ClientInfoHeading htmlFor="lastName">Last name</ClientInfoHeading>
                      <InfoInputContainer hasError={errors.lastName && touched.lastName}>
                        <InfoInput
                          mask={false}
                          value={values.lastName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="lastName"
                          id="lastName"
                          placeholder="Enter last name"
                          type="text"
                        />
                        {errors.lastName && touched.lastName && (
                          <InfoInputErrorTooltip
                            content={errors.lastName}
                            placement="left"
                            fadeDuration={100}
                            padding={20}
                            radius={6}
                            background="#ffffff"
                            color="#5a5a5a"
                            border="#ebebeb"
                          >
                            <InvalidCross color="#ff4e72" />
                          </InfoInputErrorTooltip>
                        )}
                      </InfoInputContainer>
                    </li>
                    {payingWith.value === 'newCard' && (
                      <li>
                        <ClientInfoHeading htmlFor="card">Card</ClientInfoHeading>
                        <CardInputContainer>
                          <CardInputRow>
                            <InfoInputContainer>
                              <StripeInput
                                style={{ borderTopRightRadius: 6, borderTopLeftRadius: 6 }}
                              >
                                <CardNumberElement style={{ ...baseInputStyle }} onChange={handleCardElementsInput} ref={this.cardNumber} />
                              </StripeInput>
                              <CardIcon>
                                {getCardBrandIcon(brand)}
                              </CardIcon>
                              {cardNumber && (
                                <InfoInputErrorTooltip
                                  content={cardNumber}
                                  style={{ right: getCardBrandIcon(brand) ? 70 : 20 }}
                                  placement="left"
                                  fadeDuration={100}
                                  padding={20}
                                  radius={6}
                                  background="#ffffff"
                                  color="#5a5a5a"
                                  border="#ebebeb"
                                >
                                  <InvalidCross color="#ff4e72" />
                                </InfoInputErrorTooltip>
                              )}
                            </InfoInputContainer>
                          </CardInputRow>
                          <CardInputRow>
                            <InfoInputContainer hasError={errors.cardExpiration && touched.cardExpiration}>
                              <StripeInput>
                                <CardExpiryElement style={{ ...baseInputStyle }} onChange={handleCardElementsInput} ref={this.cardExpiry} />
                              </StripeInput>
                              {cardExpiry && (
                                <InfoInputErrorTooltip
                                  content={cardExpiry}
                                  placement="left"
                                  fadeDuration={100}
                                  padding={20}
                                  radius={6}
                                  background="#ffffff"
                                  color="#5a5a5a"
                                  border="#ebebeb"
                                >
                                  <InvalidCross color="#ff4e72" />
                                </InfoInputErrorTooltip>
                              )}
                            </InfoInputContainer>
                            <InfoInputContainer hasError={errors.cvc && touched.cvc}>
                              <StripeInput>
                                <CardCVCElement style={{ ...baseInputStyle }} onChange={handleCardElementsInput} ref={this.cardCvc} />
                              </StripeInput>
                              {cardCvc && (
                                <InfoInputErrorTooltip
                                  content={cardCvc}
                                  placement="left"
                                  fadeDuration={100}
                                  padding={20}
                                  radius={6}
                                  background="#ffffff"
                                  color="#5a5a5a"
                                  border="#ebebeb"
                                >
                                  <InvalidCross color="#ff4e72" />
                                </InfoInputErrorTooltip>
                              )}
                            </InfoInputContainer>
                          </CardInputRow>
                          <CardInputRow>
                            <InfoInputContainer hasError={errors.zipCode && touched.zipCode}>
                              <StripeInput
                                style={{ borderBottomLeftRadius: 6, borderBottomRightRadius: 6 }}
                              >
                                <PostalCodeElement style={{ ...baseInputStyle }} onChange={handleCardElementsInput} ref={this.postalCode} />
                              </StripeInput>
                              {postalCode && (
                                <InfoInputErrorTooltip
                                  content={postalCode}
                                  placement="left"
                                  fadeDuration={100}
                                  padding={20}
                                  radius={6}
                                  background="#ffffff"
                                  color="#5a5a5a"
                                  border="#ebebeb"
                                >
                                  <InvalidCross color="#ff4e72" />
                                </InfoInputErrorTooltip>
                              )}
                            </InfoInputContainer>
                          </CardInputRow>
                        </CardInputContainer>
                      </li>
                    )}
                    <li style={{display: userData ? 'none' : 'list-item'}}>
                      <ClientInfoHeading htmlFor="emailAddress">E-mail</ClientInfoHeading>
                      <InfoInputContainer hasError={errors.emailAddress && touched.emailAddress}>
                        <InfoInput
                          mask={false}
                          value={values.emailAddress}
                          onChange={handleChange}
                          onBlur={evt => onEmailBlur(evt, handleBlur)}
                          name="emailAddress"
                          id="emailAddress"
                          placeholder="Enter e-mail"
                          type="email"
                        />
                        {errors.emailAddress && touched.emailAddress && (
                          <InfoInputErrorTooltip
                            content={errors.emailAddress}
                            placement="left"
                            fadeDuration={100}
                            padding={20}
                            radius={6}
                            background="#ffffff"
                            color="#5a5a5a"
                            border="#ebebeb"
                          >
                            <InvalidCross color="#ff4e72" />
                          </InfoInputErrorTooltip>
                        )}
                      </InfoInputContainer>
                      <ClientInfoNote withError={errors.emailAddress === 'User with this email already exists'}>{errors.emailAddress === 'User with this email already exists' ? errors.emailAddress : 'A booking confirmation will be sent to this address'}</ClientInfoNote>
                    </li>
                    <li>
                      <ClientInfoHeading htmlFor="phone">Phone</ClientInfoHeading>
                      <InfoInputContainer hasError={errors.phone && touched.phone}>
                        <InfoInput
                          mask={['+', '1', ' ', '(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                          guide={false}
                          value={values.phone}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="phone"
                          id="phone"
                          placeholder="+1 (800) XXX-XXXX"
                          type="tel"
                        />
                        {errors.phone && touched.phone && (
                          <InfoInputErrorTooltip
                            content={errors.phone}
                            placement="left"
                            fadeDuration={100}
                            padding={20}
                            radius={6}
                            background="#ffffff"
                            color="#5a5a5a"
                            border="#ebebeb"
                          >
                            <InvalidCross color="#ff4e72" />
                          </InfoInputErrorTooltip>
                        )}
                      </InfoInputContainer>
                    </li>
                  </ClientInfoList>
                </Fragment>
              ) : null}
            </Form>
          )}
        </Formik>
      </article>
    );
  }
}

export default PaymentWizardGift;
